import { useEffect, useState } from "react";
import { CreditCard, Banknote, MessageCircle, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const DEFAULT_METHODS = { online: true, cod: true, whatsapp: true };

const OPTIONS = [
  {
    key: "online",
    icon: CreditCard,
    title: "Pay Online (Razorpay)",
    sub: "UPI, cards and netbanking on checkout",
    testid: "pm-online",
  },
  {
    key: "cod",
    icon: Banknote,
    title: "Cash on Delivery",
    sub: "Customer pays when the order arrives",
    testid: "pm-cod",
  },
  {
    key: "whatsapp",
    icon: MessageCircle,
    title: "Order on WhatsApp",
    sub: "Sends the cart as a WhatsApp enquiry",
    testid: "pm-whatsapp",
  },
];

export default function AdminPayments() {
  const [methods, setMethods] = useState(DEFAULT_METHODS);
  const [config, setConfig] = useState({ dummy_payment: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setMethods({ ...DEFAULT_METHODS, ...(r.data.payment_methods || {}) });
    }).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const toggle = (key, on) => setMethods((m) => ({ ...m, [key]: on }));

  const save = async () => {
    if (!methods.online && !methods.cod && !methods.whatsapp) {
      return toast.error("Enable at least one payment method");
    }
    setSaving(true);
    try {
      await api.put("/admin/settings", { payment_methods: methods });
      toast.success("Payment methods saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-2xl font-bold mb-1">Payment Methods</h1>
      <p className="text-sm text-muted-foreground mb-6">Choose which options shoppers see on checkout. Disabled methods are hidden on the storefront.</p>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
        {OPTIONS.map(({ key, icon: Icon, title, sub, testid }) => (
          <div key={key} className="flex items-center gap-4 rounded-xl border border-border p-4" data-testid={testid}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${methods[key] ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{title}</p>
              <p className="text-xs text-muted-foreground">{sub}</p>
            </div>
            <Switch
              checked={Boolean(methods[key])}
              onCheckedChange={(on) => toggle(key, on)}
              data-testid={`${testid}-toggle`}
            />
          </div>
        ))}
        <button onClick={save} disabled={saving} className="mt-2 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="save-payments-btn">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 mt-5">
        <h2 className="font-semibold mb-3">Razorpay status</h2>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Online payments</span>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${config.dummy_payment ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>
            {config.dummy_payment ? "TEST / DUMMY MODE" : "LIVE"}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          {config.dummy_payment
            ? "No Razorpay keys are set, so online orders are auto-marked paid when this method is enabled. Turning Online off hides it from checkout even in test mode."
            : "Live Razorpay keys are active. Disable Online above if you do not want shoppers to pay on the site."}
        </p>
      </div>
    </div>
  );
}
